import React from "react";

import { createDialogEmitter } from "#app/lib/dialog-emitter";

// Lazy: the manage dialog pulls the whole builder (canvas, @xyflow,
// CodeMirror), so the chunk loads on first open instead of shipping in the
// always-loaded workspace layout bundle.
const AutomationsManageDialog = React.lazy(() =>
  import("#app/components/automations/automations-dialog").then((m) => ({
    default: m.AutomationsManageDialog,
  })),
);

// An optional "open straight into a screen" intent.
// - "create-for-workflow": the workflow header's Automations popover — opens a
//   new automation scoped to that workflow (so the header location + the
//   trigger/stage pickers are pre-scoped to it).
// - "automate-stage": a workflow board/list stage group menu — opens a new
//   automation scoped to the workflow with a Stage-changed trigger pinned to
//   the chosen stage (To = that stage).
// - "automate-field": a custom-field column header's "Automate field" — opens
//   a new automation scoped to the workflow with the default trigger and an
//   "Update custom field" action pre-selected to that field (the field is the
//   ACTION here, so no trigger is pinned — the stage entry's mirror image).
// - "manage-for-workflow": the same popover's "Manage automations" — lands on
//   the Manage list with the location filter pinned to that workflow.
// - "edit-automation": tapping an automation card in the popover — opens that
//   existing automation straight in the editor.
export type AutomationsDialogIntent =
  | { type: "create-for-workflow"; workflowId: string }
  | { type: "automate-stage"; workflowId: string; stageId: string }
  | { type: "automate-field"; workflowId: string; customFieldId: string }
  | { type: "manage-for-workflow"; workflowId: string }
  | { type: "edit-automation"; automationId: string };

type AutomationsDialogActions = {
  openDialog: (intent?: AutomationsDialogIntent) => void;
  closeDialog: () => void;
};

// Callers inside bottom-sheet portals cannot read React context, so open and
// close go through the emitter. The intent is the payload: null is the plain
// list entry point.
const automationsDialog = createDialogEmitter<AutomationsDialogIntent | null>("automations");

/** Get stable open/close callbacks. Works inside bottom-sheet portals. */
export function useAutomationsDialogActions(): AutomationsDialogActions {
  return React.useMemo<AutomationsDialogActions>(
    () => ({
      openDialog: (intent) => automationsDialog.open(intent ?? null),
      closeDialog: automationsDialog.close,
    }),
    [],
  );
}

export function AutomationsDialog() {
  // Nothing mounts until the first open, so closed dialogs (and the lazy
  // chunk) cost nothing at app load. The intent survives close so the closing
  // animation does not flash the list.
  const { open, payload: intent, everOpened } = automationsDialog.useDialogState();
  if (!everOpened) return null;

  return (
    <React.Suspense fallback={null}>
      <AutomationsManageDialog
        open={open}
        intent={intent}
        onClose={automationsDialog.close}
      />
    </React.Suspense>
  );
}
